import { Box, Container, Flex, Text, Button, Loader, Center, Title } from "@mantine/core";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import AnonymousProfile from "@/components/shared/AnonymousCompanyProfile/AnonymousProfile";
import MatchedProfileView from "@/pages/profile/MatchedView";
import { IconDownload, IconSend } from "@tabler/icons-react";
import { downloadAnonymizedProfilePdf } from "@/utils/anonymizedProfilePdf";
import { errorNotification } from "@/shared/utils/errorNotification";
import ApplyVerificationModal from "../profile-verification/ApplyVerificationModal";
import VerificationSubmittedModal from "@/pages/profile-verification/VerificationSubmittedModal";
import RejectionDetailsModal from "@/pages/profile-verification/VerificationRejectedModal";
import VerificationRejectedBanner from "../profile-verification/VerificationRejectedBanner";
import { useUserContext } from "@/context/UserContext";

export default function CompanyProfileView() {
  const navigate = useNavigate();
  const { userDetails, hasProfile } = useUserContext();
  const [activeView, setActiveView] = useState<"anonymized" | "matched">("anonymized");
  const [company, setCompany] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [verificationModalOpened, setVerificationModalOpened] = useState(false);
  const [verificationSubmittedModalOpened, setVerificationSubmittedModalOpened] = useState(false);
  const [rejectionModalOpened, setRejectionModalOpened] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [verificationData, setVerificationData] = useState<any>(null);
  const [anonymizedPdfLoading, setAnonymizedPdfLoading] = useState(false);

  const fetchCompanyData = useCallback(async () => {
    if (hasProfile === null) return;
    const companyId = userDetails?.company?.id;

    if (!companyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const companyData = await trpc.company.getById.query({ companyId });
      setCompany(companyData);
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  }, [hasProfile, userDetails?.company?.id]);

  useEffect(() => {
    fetchCompanyData();
  }, [fetchCompanyData]);

  const handleApplyForVerification = async () => {
    if (!company?.id) return;

    setIsApplying(true);
    try {
      const result = await trpc.verification.verifyCompany.mutate({
        legalName: company.legalName,
        registrationNumber: company.registrationNumber,
        headquartersCountry: company.headquartersCountry,
        state: company.state ?? undefined,
      });

      if (result?.success) {
        setVerificationData({
          ...result.data,
          _verificationSource: result.source,
        });
        setVerificationModalOpened(true);
      }

      await fetchCompanyData(); // Refresh the profile to capture SYSTEM_VERIFIED status or UNDER_REVIEW
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setIsApplying(false);
    }
  };

  const handleCloseVerificationModal = () => {
    setVerificationModalOpened(false);
    setTimeout(() => setVerificationData(null), 200);
  };

  if (loading) {
    return (
      <Center py="xl" mih="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!company) {
    return (
      <Container size="xl" py="xl">
        <Text>Company not found. Please create your company profile first.</Text>
        <Button mt="md" onClick={() => navigate("/create-profile")}>
          Go to Dashboard
        </Button>
      </Container>
    );
  }

  const companyStatus = company.status;
  const isRejected = companyStatus === "ACTION_REQUIRED";
  const canApplyForVerification = companyStatus === "DRAFT" || companyStatus === "ACTION_REQUIRED";

  const renderHeader = () => (
    <>
      <Box py="md" style={{ borderBottom: "1px solid #E5E7EB" }}>
        <Flex
          justify="space-between"
          pl={20}
          pr={20}
          align="flex-start"
          direction={{ base: "column", sm: "row" }}
          gap="sm"
        >
          <Title order={2} fw={400} fz={24}>
              Welcome! {company.legalName}
          </Title>
          <Flex
            gap="sm"
            align={{ base: "stretch", sm: "center" }}
            direction={{ base: "column", sm: "row" }}
            w={{ base: "100%", sm: "auto" }}
          >
            {canApplyForVerification && (
              <Button
                color="#34a853"
                size="sm"
                fz="sm"
                leftSection={<IconSend size={18} />}
                onClick={() => setVerificationModalOpened(true)}
                style={{ whiteSpace: "nowrap" }}
                w={{ base: "100%", sm: "auto" }}
              >
                Apply for Verification
              </Button>
            )}
          </Flex>
        </Flex>
      </Box>

      <Box py="md">
        <Container size="fluid">
          <Flex direction="column" gap="sm">
            <Flex justify="space-between" align="center" wrap="wrap" gap="sm">
              <Flex
                direction="column"
                gap="xs"
                style={{ backgroundColor: "#F3F4F6", padding: "8px", borderRadius: "10px", width: "fit-content" }}
              >
                <Flex gap="xs" align="flex-start" wrap="wrap">
                  <Button
                    variant={activeView === "anonymized" ? "filled" : "light"}
                    color="gray"
                    size="md"
                    w={560}
                    onClick={() => setActiveView("anonymized")}
                    styles={{
                      root: {
                        width: "560px",
                        minHeight: "74px",
                        height: "auto",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        justifyContent: "center",
                        backgroundColor:
                          activeView === "anonymized" ? "var(--mantine-color-primary-6)" : "#FFFFFF",
                        color: activeView === "anonymized" ? "#FFFFFF" : "#1F2937",
                        fontWeight: 500,
                        border: "none",
                        boxShadow:
                          activeView === "anonymized"
                            ? "0px 1px 3px rgba(37, 99, 235, 0.35), 0px 1px 2px rgba(37, 99, 235, 0.25)"
                            : "none",
                        "&:hover": {
                          backgroundColor:
                            activeView === "anonymized" ? "var(--mantine-color-primary-7)" : "#F9FAFB",
                        },
                      },
                    }}
                  >
                    <Flex direction="column" gap={2} align="center">
                      <Text fw={500} size="sm" c={activeView === "anonymized" ? "#FFFFFF" : "#1F2937"}>
                        Anonymized View
                      </Text>
                      <Text size="xs" c={activeView === "anonymized" ? "rgba(255,255,255,0.92)" : "#6B7280"}>
                        How your company profile appears to others before revealing your identity.
                      </Text>
                    </Flex>
                  </Button>
                  <Button
                    variant={activeView === "matched" ? "filled" : "light"}
                    color="gray"
                    size="md"
                    w={560}
                    onClick={() => setActiveView("matched")}
                    styles={{
                      root: {
                        width: "560px",
                        minHeight: "74px",
                        height: "auto",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        justifyContent: "center",
                        backgroundColor:
                          activeView === "matched" ? "var(--mantine-color-primary-6)" : "#FFFFFF",
                        color: activeView === "matched" ? "#FFFFFF" : "#1F2937",
                        fontWeight: 500,
                        border: "none",
                        boxShadow:
                          activeView === "matched"
                            ? "0px 1px 3px rgba(37, 99, 235, 0.35), 0px 1px 2px rgba(37, 99, 235, 0.25)"
                            : "none",
                        "&:hover": {
                          backgroundColor:
                            activeView === "matched" ? "var(--mantine-color-primary-7)" : "#F9FAFB",
                        },
                      },
                    }}
                  >
                    <Flex direction="column" gap={2} align="center">
                      <Text fw={500} size="sm" c={activeView === "matched" ? "#FFFFFF" : "#1F2937"}>
                        Matched View
                      </Text>
                      <Text size="xs" c={activeView === "matched" ? "rgba(255,255,255,0.92)" : "#6B7280"}>
                        How your full company profile is shared after a mutual match is established.
                      </Text>
                    </Flex>
                  </Button>
                  {activeView === "anonymized" && (
                    <Button
                      variant="outline"
                      color="primary"
                      size="md"
                      leftSection={<IconDownload size={18} />}
                      loading={anonymizedPdfLoading}
                      onClick={async () => {
                        if (!company) return;
                        setAnonymizedPdfLoading(true);
                        try {
                          await downloadAnonymizedProfilePdf(company);
                        } catch (err) {
                          errorNotification({
                            title: "Could not create PDF",
                            message: err instanceof Error ? err.message : "Please try again.",
                          });
                        } finally {
                          setAnonymizedPdfLoading(false);
                        }
                      }}
                      style={{ alignSelf: "center", minHeight: "74px" }}
                    >
                      Download anonymized PDF
                    </Button>
                  )}
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Container>
      </Box>

      {isRejected && (
        <Container size="fluid" mb="md">
          <VerificationRejectedBanner onKnowMore={() => setRejectionModalOpened(true)} />
        </Container>
      )}
    </>
  );

  return (
    <Box>
      {renderHeader()}

      {activeView === "anonymized" ? (
        <AnonymousProfile pageID="MY_PROFILE_PREVIEW" companyData={company} />
      ) : (
        <MatchedProfileView company={company} containerSize="fluid" isOwnProfile={true} />
      )}

      <ApplyVerificationModal
        opened={verificationModalOpened}
        onClose={handleCloseVerificationModal}
        onApply={handleApplyForVerification}
        isLoading={isApplying}
        verificationData={verificationData}
      />

      <VerificationSubmittedModal
        opened={verificationSubmittedModalOpened}
        onClose={() => setVerificationSubmittedModalOpened(false)}
      />

      <RejectionDetailsModal
        opened={rejectionModalOpened}
        onClose={() => setRejectionModalOpened(false)}
        adminComments={company?.adminComments}
      />
    </Box>
  );
}
